import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

// Default icons for different alert types
const defaultIcons = {
  success: "fa-solid fa-check-circle text-green-600",
  error: "fa-solid fa-exclamation-circle text-red-600", 
  warning: "fa-solid fa-exclamation-triangle text-yellow-600",
  info: "fa-solid fa-info-circle text-blue-600 dark:text-cyan-500",
};

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 transition-all duration-200",
  {
    variants: {
      variant: {
        success: "border-success/20 bg-success/10 text-green-800 dark:bg-success/10 dark:text-green-400 dark:border-success/20",
        error: "border-danger/20 bg-danger/10 text-red-800 dark:bg-danger/10 dark:text-red-400 dark:border-danger/20", 
        warning: "border-warning/20 bg-warning/10 text-yellow-800 dark:bg-warning/10 dark:text-yellow-400 dark:border-warning/20",
        info: "border-info/20 bg-info/10 text-blue-800 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20",
        default: "border-gray-200 bg-gray-50 text-gray-800 dark:border-dark-300 dark:bg-dark-200 dark:text-dark-800",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export const Alert = React.forwardRef(({
  className,
  variant = "default",
  title,
  children,
  icon,
  dismissible = false,
  onDismiss,
  ...props
}, ref) => {
  const IconComponent = icon || (defaultIcons[variant] && (
    <i className={defaultIcons[variant]} />
  ));

  return (
    <div
      ref={ref}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <div className="flex">
        {IconComponent && (
          <div className="flex-shrink-0 mr-3">
            {IconComponent}
          </div>
        )}
        
        <div className="flex-1">
          {title && (
            <h5 className="mb-1 font-medium leading-none tracking-tight">
              {title}
            </h5>
          )}
          <div className="text-sm [&_p]:leading-relaxed">
            {children}
          </div>
        </div>
        
        {dismissible && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 ml-4 inline-flex h-4 w-4 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <i className="fa-solid fa-times h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
});

Alert.displayName = "Alert";

// Pre-configured alert variants for common use cases
export const SuccessAlert = React.forwardRef((props, ref) => (
  <Alert ref={ref} variant="success" {...props} />
));

SuccessAlert.displayName = "SuccessAlert";

export const ErrorAlert = React.forwardRef((props, ref) => (
  <Alert ref={ref} variant="error" {...props} />
));

ErrorAlert.displayName = "ErrorAlert";

export const WarningAlert = React.forwardRef((props, ref) => (
  <Alert ref={ref} variant="warning" {...props} />
));

WarningAlert.displayName = "WarningAlert";

export const InfoAlert = React.forwardRef((props, ref) => (
  <Alert ref={ref} variant="info" {...props} />
));

InfoAlert.displayName = "InfoAlert"; 